import { ThemeProvider } from 'emotion-theming';
import React from 'react';
import { StaticQuery, graphql } from 'gatsby';
import styled from '@emotion/styled';
import ResponsiveSidebar from './responsive-sidebar';
import FlexWrapper from './flex-wrapper';
import Sidebar from './sidebar';
import SidebarNav from './sidebar-nav/index';
import { OuterContentWrapper, BackgroundImage } from './content-wrapper';
import { StyledMobileHeader } from './header';
import siteTheme from '../util/themes';
import { normalize } from '../util/normalize';

import { StaticNavContents, GameNavContents } from '../util/nav';
import { extractRootPath } from '../util/current-page';
import SEO from '../util/seo';
import { AuthorProvider } from '../context/author-context';
import { PageProvider } from '../context/page-context';
import gameThemes from '../util/game-themes';


const BaseStyle = styled.div(props => ({
  color: props.theme.text,
  [`a`]: {
    color: props.theme.link
  },
  [`a:visited`]: {
    color: props.theme.link
  },
  [`a:-webkit-any-link`]: {
    color: props.theme.link
  }
}));

const Layout = props => {
  let { gameName } = props;
  let gameID;
  const { hideBottomNav, pageType, title, pageStructuredData, description } = props;

  const gameTheme = gameThemes(gameName);

  return (
    <StaticQuery
      query={graphql`
        {
          site {
            siteMetadata {
              defaultBackgroundImageURI
            }
          }
          cms {
            games(sort: "name") {
              id
              name
              discordInvite
              backgroundURI
              featured
              EntityConfigs {
                singular
                plural
                primary
              }
              iconSmall {
                url
              }
            }
          }
        }
      `}
      render={data => {
        const { defaultBackgroundImageURI } = data.site.siteMetadata;
        const { games } = data.cms;

        const featuredGames = games.filter(game => game.featured);
        let currentGame;

        let backgroundURI = defaultBackgroundImageURI;
        let iconSmall;
        let gameIconURI;
        let EntityConfigs;
        let discordInvite;

        const rootPath =
          props.location && props.location.pathname && extractRootPath(props.location.pathname);

        if (rootPath) {
          currentGame = games.find(game => {
            return normalize.hyphenateAndLowerCase(game.name) === rootPath;
          });

          if (currentGame) {
            gameName = currentGame.name;
            gameID = currentGame.id;
            EntityConfigs = currentGame.EntityConfigs;
            discordInvite = currentGame.discordInvite;
            ({ backgroundURI, iconSmall } = currentGame);
            if (iconSmall) {
              gameIconURI = iconSmall.url;
            }
          }
        }

        const gameHubNavContents = GameNavContents(featuredGames);

        // Combine the site, and optionally, game theme
        const aggregateTheme = { ...(gameTheme || {}), ...siteTheme('dark') };
        const color = aggregateTheme.primaryAccent || aggregateTheme.primary;

        return (
          <>
            <SEO
              location={props.location.pathname}
              title={title}
              description={description}
              shareImageURI={props.shareImageURI}
              color={color}
              noIndex={props.noIndex}
              pageStructuredData={pageStructuredData}
            />
            <PageProvider
              pageType={pageType}
              pageTitle={title}
              gameName={gameName}
              gameID={gameID}
              EntityConfigs={EntityConfigs}
              discordInvite={discordInvite}
              path={props.location.pathname}
              pageProps={props.pageProps}
            >
              <ThemeProvider theme={aggregateTheme}>
                <AuthorProvider>
                  <BaseStyle>
                    <BackgroundImage uri={backgroundURI} />
                    <ResponsiveSidebar>
                      {({ sidebarRef, onWrapperClick, sidebarOpen, openSidebar }) => (
                        <FlexWrapper onClick={onWrapperClick}>
                          <Sidebar
                            noLogo
                            ref={sidebarRef}
                            open={sidebarOpen}
                            enableCollapse={props.enableCollapsedSidebar}
                          >
                            <SidebarNav
                              contents={StaticNavContents}
                              rootPath="/"
                              browserPath={props.location.pathname}
                            />

                            <SidebarNav
                              title="See all Game Wikis"
                              contents={gameHubNavContents}
                              browserPath={props.location.pathname}
                              rootPath="/"
                            />
                          </Sidebar>
                          <OuterContentWrapper tabIndex="0" flush>
                            <StyledMobileHeader
                              gameName={currentGame && currentGame.name}
                              pathName={props.location.pathname}
                              EntityConfigs={EntityConfigs}
                              hideLogo={props.hideHeaderLogo}
                              hideBottomNav={hideBottomNav}
                              gameIconURI={gameIconURI}
                              onMenuClick={openSidebar}
                            />
                            {props.children}
                          </OuterContentWrapper>
                        </FlexWrapper>
                      )}
                    </ResponsiveSidebar>
                  </BaseStyle>
                </AuthorProvider>
              </ThemeProvider>
            </PageProvider>
          </>
        );
      }}
    />
  );
};

export default Layout;
